import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FEATURE_BULK_ACTIONS_V2 } from '../../../../flags';
import { ActionListHistory, ActionListsHistoryStoreService } from '../../action-lists-history.store.service';
import { ActionListTagsDialogComponent } from '../../action-lists-tags-dialog.component';
import { BulkActionsProgressReporter } from '../../bulk-actions-reporter.service';
import { HistoryItemsProgressReporter } from '../../history-items-reporter.service';
import { ProgressReporter } from '../../progress-reporter.service';
import { ToggleEvent } from '../common';

function isImport(type: string): boolean {
  return type === 'ImportListHistoryItemModel';
}

@Component({
  selector: 'app-actions-menu',
  templateUrl: './actions.component.html',
  styleUrls: ['actions.component.scss'],
})
export class ActionsComponent {
  readonly enableMenu$: Observable<boolean>;
  @Input() isErrorShowing = false;
  @Output() errorsToggled = new EventEmitter<ToggleEvent>();
  private historyItemId$$ = new ReplaySubject<string>(1);
  private itemData$$ = new ReplaySubject<ActionListHistory>(1);
  readonly actions$ = this.itemData$$.pipe(map((v) => v.actions));
  private _itemData: ActionListHistory;

  constructor(
    private actionListHistoryStoreService: ActionListsHistoryStoreService,
    @Inject(HistoryItemsProgressReporter) private readonly legacyProgress: ProgressReporter,
    @Inject(BulkActionsProgressReporter) private readonly bulkProgress: ProgressReporter,
    @Inject(FEATURE_BULK_ACTIONS_V2) private readonly isBulkV2Enabled$: Observable<boolean>,
    private dialog: MatDialog,
  ) {
    this.enableMenu$ = this.buildEnableMenu$();
  }

  private buildEnableMenu$(): Observable<boolean> {
    return combineLatest([this.historyItemId$$, this.itemData$$, this.isBulkV2Enabled$]).pipe(
      switchMap(([id, itemData, v2Enabled]) => {
        if (itemData.actions.length === 0) {
          return of(false);
        }
        let progress = this.legacyProgress;
        if (v2Enabled && !isImport(itemData.type)) {
          progress = this.bulkProgress;
        }
        return progress.getPolledStatus$(id).pipe(
          // FIXME: We'll need check if the job is "finalized" before files can be downloaded
          // This will require some extra info to be added at in various places
          map((s) => s.isFinalized),
        );
      }),
    );
  }

  @Input() set historyItemId(id: string) {
    this.historyItemId$$.next(id);
  }

  @Input() set itemData(data: ActionListHistory) {
    this.itemData$$.next(data);
    this._itemData = data;
  }

  downloadOriginalFile(): void {
    this.actionListHistoryStoreService.downloadFile(this._itemData.originalFile);
  }

  downloadSkippedFile(): void {
    this.actionListHistoryStoreService.downloadFile(this._itemData.skippedFile);
  }

  downloadNAPDataFile(): void {
    this.actionListHistoryStoreService.downloadFile(this._itemData.napDataFile);
  }

  redirectToBrandReport(): void {
    window.open(this._itemData.reportUrl, '_blank');
  }

  viewTags(): void {
    this.dialog.closeAll();
    this.dialog.open(ActionListTagsDialogComponent, {
      width: '620px',
      data: { tags: this._itemData.tags },
    });
  }

  toggleInlineErrors(): void {
    this.errorsToggled.next({
      itemId: this._itemData.itemId,
      showErrors: !this.isErrorShowing,
    });
  }
}
